import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ExternalLink, Info, AlertTriangle, Gift } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ASTRO_INTUITO, ASTRO_PERFIL, ASTRO_INTERACAO, ASTRO_PLAYLISTS } from "@/lib/constants";

const Option = ({ active, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`w-full rounded-xl border px-4 py-3 text-left text-sm transition-colors ${active ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)] text-[color:var(--brand-graphite)]" : "border-[color:var(--brand-border)] bg-white text-[color:var(--brand-graphite)] hover:bg-[color:var(--brand-paper)]"}`}>
    <span className="flex items-center gap-3">
      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${active ? "border-[color:var(--brand-marsala)]" : "border-[color:var(--brand-border)]"}`}>
        {active && <span className="h-2 w-2 rounded-full bg-[color:var(--brand-marsala)]" />}
      </span>
      {children}
    </span>
  </button>
);

const Pill = ({ active, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`rounded-full border px-3.5 py-2 text-sm transition-colors ${active ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)] text-[color:var(--brand-marsala)]" : "border-[color:var(--brand-border)] bg-white text-[color:var(--brand-graphite)] hover:bg-[color:var(--brand-paper)]"}`}>{children}</button>
);

export const AstroLiveStep = ({ astro, products, onChange }) => {
  const a = astro || {};
  const p = products || {};
  const set = (k, v) => onChange({ ...a, [k]: v });
  const isGospel = a.perfil_evento === "Gospel";
  const showBananinha = !isGospel; // bebida (bananinha) não se aplica a eventos gospel
  const perfil = a.perfil_evento;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="mx-auto max-w-2xl">
      <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-[color:var(--brand-graphite)] sm:text-3xl"><Sparkles className="h-6 w-6 text-[color:var(--brand-marsala)]" /> AstroLive</h2>
      <p className="mt-2 text-sm text-[color:var(--brand-muted)]">Agora vamos entender a energia que vocês imaginam para o show.</p>

      <div className="mt-6">
        <Label>Qual é o principal intuito do show?</Label>
        <div className="mt-2 space-y-2">
          {ASTRO_INTUITO.map((o) => <Option key={o} active={a.intuito === o} onClick={() => set("intuito", o)} testId={`astro-intuito-${o.slice(0, 10)}`}>{o}</Option>)}
        </div>
        {a.intuito === "Outro." && (
          <Textarea data-testid="astro-intuito-outro" value={a.intuito_outro || ""} onChange={(e) => set("intuito_outro", e.target.value)} placeholder="Conte para nós como vocês imaginam o show" className="mt-3" />
        )}
      </div>

      <div className="mt-8 rounded-2xl border border-[color:var(--brand-border)] bg-[color:var(--brand-paper)] p-4">
        <h3 className="font-serif text-lg font-semibold text-[color:var(--brand-graphite)]">Repertório e identidade do evento</h3>
        <Label className="mt-3 block">Qual será o perfil musical do evento?</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {ASTRO_PERFIL.map((o) => <Pill key={o} active={perfil === o} onClick={() => set("perfil_evento", o)} testId={`astro-perfil-${o}`}>{o}</Pill>)}
        </div>
        <div className="mt-3 space-y-2">
          {(perfil === "Secular" || perfil === "Misto") && (
            <a href={ASTRO_PLAYLISTS.Secular} target="_blank" rel="noreferrer" data-testid="astro-playlist-secular" className="inline-flex items-center gap-2 rounded-lg border border-[color:var(--brand-border)] bg-white px-3 py-2 text-sm text-[color:var(--brand-marsala)] hover:bg-[color:var(--brand-marsala-soft)]">
              <ExternalLink className="h-4 w-4" /> Ouvir playlist de referência — AstroLive Secular
            </a>
          )}
          {(perfil === "Gospel" || perfil === "Misto") && (
            <a href={ASTRO_PLAYLISTS.Gospel} target="_blank" rel="noreferrer" data-testid="astro-playlist-gospel" className="inline-flex items-center gap-2 rounded-lg border border-[color:var(--brand-border)] bg-white px-3 py-2 text-sm text-[color:var(--brand-marsala)] hover:bg-[color:var(--brand-marsala-soft)]">
              <ExternalLink className="h-4 w-4" /> Ouvir playlist de referência — AstroLive Gospel
            </a>
          )}
        </div>
      </div>

      <div className="mt-6"><Label>Existe alguma música, artista ou estilo que gostariam que estivesse presente no show?</Label>
        <Textarea data-testid="astro-desejadas" value={a.musicas_desejadas || ""} onChange={(e) => set("musicas_desejadas", e.target.value)} placeholder="Ex.: Bruno Mars, Dua Lipa, músicas dos anos 2000 e funk antigo." className="mt-1.5" /></div>

      <div className="mt-4"><Label>Existe alguma música, artista ou estilo que vocês NÃO querem ouvir?</Label>
        <Textarea data-testid="astro-nao-desejadas" value={a.musicas_nao_desejadas || ""} onChange={(e) => set("musicas_nao_desejadas", e.target.value)} placeholder="Pode ser um artista, estilo ou uma música a evitar." className="mt-1.5" /></div>

      <div className="mt-4"><Label>Link de playlist para inspiração (opcional)</Label>
        <div className="relative mt-1.5">
          <ExternalLink className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--brand-muted)]" />
          <Input data-testid="astro-playlist-link" type="url" value={a.playlist_link || ""} onChange={(e) => set("playlist_link", e.target.value)} placeholder="Cole aqui o link (Spotify, YouTube, Deezer...)" className="h-11 pl-9" />
        </div>
        <p className="mt-1 text-[11px] text-[color:var(--brand-muted)]">Se tiverem uma playlist que traduz o clima que desejam, compartilhe com a gente.</p>
      </div>

      <div className="mt-6">
        <Label>Sobre interação com os convidados, vocês preferem:</Label>
        <div className="mt-2 space-y-2">
          {ASTRO_INTERACAO.map((o) => <Option key={o} active={a.interacao === o} onClick={() => set("interacao", o)} testId={`astro-interacao-${o.slice(0, 8)}`}>{o}</Option>)}
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-[color:var(--brand-champagne)] bg-[color:var(--brand-champagne-soft)] p-4">
        <h3 className="flex items-center gap-2 font-serif text-lg font-semibold text-[color:var(--brand-graphite)]"><Sparkles className="h-4 w-4 text-[color:var(--brand-marsala)]" /> Autorizações</h3>
        {showBananinha && (
          <div className="mt-3">
            <Label>Está autorizada a bebida alcoólica (bananinha) durante o show?</Label>
            <div className="mt-2 flex gap-2">
              <Pill active={a.bananinha_autorizada === "sim"} onClick={() => set("bananinha_autorizada", "sim")} testId="astro-bananinha-sim">Sim</Pill>
              <Pill active={a.bananinha_autorizada === "nao"} onClick={() => set("bananinha_autorizada", "nao")} testId="astro-bananinha-nao">Não</Pill>
            </div>
            <p className="mt-1 text-[11px] text-[color:var(--brand-muted)]">* Sujeito às regras do local e à natureza do evento.</p>
          </div>
        )}
        <div className="mt-4">
          <Label>Está autorizado o uso de areia nos tambores de LED durante a apresentação?</Label>
          <div className="mt-2 flex gap-2">
            <Pill active={a.areia_autorizada === "sim"} onClick={() => set("areia_autorizada", "sim")} testId="astro-areia-sim">Sim</Pill>
            <Pill active={a.areia_autorizada === "nao"} onClick={() => set("areia_autorizada", "nao")} testId="astro-areia-nao">Não</Pill>
          </div>
          <p className="mt-1 text-[11px] text-[color:var(--brand-muted)]">* A utilização estará sujeita às regras do espaço e às condições técnicas do evento.</p>
        </div>
        <div className="mt-4">
          <Label>Está autorizado o uso da Bazuca de CO₂ durante o show?</Label>
          <div className="mt-2 flex gap-2">
            <Pill active={a.bazuca_autorizada === "sim"} onClick={() => set("bazuca_autorizada", "sim")} testId="astro-bazuca-sim">Sim</Pill>
            <Pill active={a.bazuca_autorizada === "nao"} onClick={() => set("bazuca_autorizada", "nao")} testId="astro-bazuca-nao">Não</Pill>
          </div>
          <p className="mt-1 text-[11px] text-[color:var(--brand-muted)]">* A utilização também poderá depender das regras e autorizações do local do evento.</p>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-[color:var(--brand-border)] bg-white p-4">
        <h3 className="flex items-center gap-2 font-serif text-lg font-semibold text-[color:var(--brand-graphite)]"><Gift className="h-4 w-4 text-[color:var(--brand-marsala)]" /> Brindes</h3>
        <Label className="mt-3 block">O pacote contratado contém brindes?</Label>
        <div className="mt-2 flex gap-2">
          <Pill active={a.brinde === "sim"} onClick={() => set("brinde", "sim")} testId="astro-brinde-sim">Sim</Pill>
          <Pill active={a.brinde === "nao"} onClick={() => set("brinde", "nao")} testId="astro-brinde-nao">Não</Pill>
        </div>
        {a.brinde === "sim" && (
          <div className="mt-4 space-y-3">
            <p className="text-xs text-[color:var(--brand-muted)]">Informe a quantidade de cada brinde contratado:</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {[["brinde_oculos", "Óculos"], ["brinde_bastoes", "Bastões de LED"], ["brinde_bone", "Boné"]].map(([k, lbl]) => (
                <div key={k}>
                  <Label className="text-sm">{lbl}</Label>
                  <Input data-testid={`astro-${k}`} type="number" min="0" inputMode="numeric" value={a[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder="Qtd." className="mt-1 h-11" />
                </div>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Label className="text-sm">Outro brinde</Label>
                <Input data-testid="astro-brinde-outro" value={a.brinde_outro || ""} onChange={(e) => set("brinde_outro", e.target.value)} placeholder="Ex.: pulseira, máscara..." className="mt-1 h-11" />
              </div>
              <div>
                <Label className="text-sm">Quantidade</Label>
                <Input data-testid="astro-brinde-outro-qtd" type="number" min="0" inputMode="numeric" value={a.brinde_outro_qtd || ""} onChange={(e) => set("brinde_outro_qtd", e.target.value)} placeholder="Qtd." className="mt-1 h-11" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6"><Label>Observações (opcional)</Label>
        <Textarea data-testid="astro-obs" value={a.observacoes || ""} onChange={(e) => set("observacoes", e.target.value)} className="mt-1.5" /></div>
    </motion.div>
  );
};
